import React, { FC, useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react';
import { BackTop, Layout, Drawer } from 'antd';
import { useStores } from '@app/hooks/stores';
import { config } from '@app/utils';
import { PATH } from '@app/constants';
import { Bread, Header, SiderMenuItem, Sider } from '@app/common/Layout';
import { GlobalFooter } from '../../common/GlobalFooter';

import './index.less';
import './page.less';
import { ThemesEnum } from '@app/modules/Auth';
import useInactivityLogoutTimer from '@app/hooks/useInactivityLogoutTimer';
import { parseJwtToken } from '@app/utils/helpers';

const { Content } = Layout;

interface LayoutPageProps {
  routes: SiderMenuItem[];
}

const Page: FC<LayoutPageProps> = ({ routes }) => {
  const { authStore } = useStores();
  const { collapsed, setCollapsed, logout } = authStore;
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, isMobile } = authStore;

  const onSignOut = () => logout().then(() => navigate(PATH.LOGIN));

  useInactivityLogoutTimer(onSignOut);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('___t') || '{}');

    const parsedToken = parseJwtToken(data?.access_token);

    if (parsedToken.isExpired) {
      onSignOut();
    }

    if (location.pathname === '/') {
      navigate(PATH.DASHBOARD);
    }
  }, [navigate, location]);

  // mobile view check
  useLayoutEffect(() => {
    window.addEventListener('resize', authStore.getGlobalState);
    return () => window.removeEventListener('resize', authStore.getGlobalState);
  }, []);

  const notifications = [];

  const user = authStore.userInfo;

  const newRouteList =
    locale !== 'en_US'
      ? routes.map(item => {
          const { name, ...other } = item;
          return {
            ...other,
            name: (item[locale] || {}).name || name
          };
        })
      : routes;

  const menus = newRouteList.filter(_ => _.menuParentId !== '-1');

  const headerProps = {
    theme: ThemesEnum.LIGHT,
    menus,
    collapsed,
    notifications,
    onCollapseChange: setCollapsed,
    username: user?.username,
    fixed: config.fixedHeader,
    onAllNotificationsRead: () => {
      console.warn('onAllNotificationsRead() called');
    },
    onSignOut
  };

  const siderProps = {
    theme: authStore.theme,
    menus,
    isMobile,
    collapsed,
    onCollapseChange: setCollapsed,
    onThemeChange: authStore.setTheme
  };

  return (
    <Layout>
      {isMobile ? (
        <Drawer
          maskClosable
          closable={false}
          onClose={() => setCollapsed(!collapsed)}
          visible={!collapsed}
          placement="left"
          width={200}
          style={{
            padding: 0,
            height: '100vh'
          }}
        >
          <Sider {...siderProps} collapsed={false} />
        </Drawer>
      ) : (
        <Sider {...siderProps} />
      )}
      <div className="container" style={{ paddingTop: config.fixedHeader ? 72 : 0 }} id="primaryLayout">
        <Header {...headerProps} />
        <Content className="content">
          <Bread routeList={newRouteList} />
          <Outlet />
        </Content>
        <BackTop className="backTop" target={() => document.querySelector('#primaryLayout') as HTMLElement} />
        <GlobalFooter className="main-footer" copyright={config.copyright} />
      </div>
    </Layout>
  );
};

export default observer(Page);
